<template>
  <base-modal
    ref="core"
    :nested="false"
  >
    <template #header>
      <h4 v-if="isEdit && imei">
        {{ $t('components.notificationsSettings.modals.editNotification') }} {{ imei }}
      </h4>
    </template>

    <template #body>
      <div
        class="form-horizontal"
        role="form"
      >
        <div class="form-body">
          <div class="row">
            <!-- Send by Email -->
            <div class="col-md-6 col-12 form-check d-flex align-items-center mb-3">
              <input
                type="checkbox"
                id="send-by-email"
                name="send_by_email"
                v-model="notificationSetting.send_by_email"
                class="form-check-input me-2"
              >
              <label
                for="send-by-email"
                class="form-check-label control-label"
              >{{ $t('components.notificationsSettings.dataGrids.sendByEmail') }}</label>
            </div>

            <!-- Send by SMS -->
            <div class="col-md-6 col-12 form-check d-flex align-items-center mb-3">
              <input
                type="checkbox"
                id="send-by-sms"
                name="send_by_sms"
                v-model="notificationSetting.send_by_sms"
                class="form-check-input me-2"
              >
              <label
                for="send-by-sms"
                class="form-check-label control-label"
              >{{ $t('components.notificationsSettings.dataGrids.sendBySms') }}</label>
            </div>

            <!-- Send by Mercure -->
            <div class="col-md-6 col-12 form-check d-flex align-items-center mb-3">
              <input
                type="checkbox"
                id="send-by-mercure"
                name="send_by_mercure"
                v-model="notificationSetting.send_by_mercure"
                class="form-check-input me-2"
              >
              <label
                for="send-by-mercure"
                class="form-check-label control-label"
              >{{ $t('components.notificationsSettings.dataGrids.sendByMercure') }}</label>
            </div>

            <!-- Ignition -->
            <div class="col-md-6 col-12 form-check d-flex align-items-center mb-3">
              <input
                type="checkbox"
                id="ignition"
                name="ignition"
                v-model="notificationSetting.ignition"
                class="form-check-input me-2"
              >
              <label
                for="ignition"
                class="form-check-label control-label"
              >{{ $t('components.notificationsSettings.modals.ignition') }}</label>
            </div>

            <!-- Satellites -->
            <div class="col-md-6 col-12 form-check d-flex align-items-center mb-3">
              <input
                type="checkbox"
                id="satellites"
                name="satellites"
                v-model="notificationSetting.satellites"
                class="form-check-input me-2"
              >
              <label
                for="satellites"
                class="form-check-label control-label"
              >{{ $t('components.notificationsSettings.modals.satellites') }}</label>
            </div>
          </div>

          <!-- Speed and Voltage -->
          <div class="row mt-3 input-section">
            <!-- Speed -->
            <div class="col-md-6 col-12">
              <div class="form-check d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  id="speed-checkbox"
                  name="speed-checkbox"
                  v-model="isSpeedChecked"
                  class="form-check-input me-2"
                >
                <label
                  for="speed-checkbox"
                  class="form-check-label control-label"
                >
                  {{ $t('components.notificationsSettings.modals.speed') }}
                </label>
              </div>
              <input
                type="text"
                id="speed-input"
                name="speed"
                v-model="v$.notificationSetting.speed.$model"
                :disabled="!isSpeedChecked"
                class="form-control"
                @blur="v$.notificationSetting.speed.$touch()"
                :class="{
                  'text-muted': !isSpeedChecked,
                  'is-invalid':
                    v$.notificationSetting.speed.$error &&
                    v$.notificationSetting.speed.$dirty,
                }"
                :placeholder="!isSpeedChecked ? $t('components.notificationsSettings.modals.enterSpeed') : ''"
              >
              <!-- Validation message with space reserved below -->
              <div class="validation-space">
                <div
                  v-if="
                    v$.notificationSetting.speed.$error &&
                      v$.notificationSetting.speed.$dirty
                  "
                  class="invalid-feedback mt-2 d-block"
                >
                  <div
                    v-for="error in v$.notificationSetting.speed.$errors"
                    :key="error.$uid"
                  >
                    {{ error.$message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Voltage -->
            <div class="col-md-6 col-12">
              <div class="form-check d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  id="voltage-checkbox"
                  name="voltage-checkbox"
                  v-model="isVoltageChecked"
                  class="form-check-input me-2"
                >
                <label
                  for="voltage-checkbox"
                  class="form-check-label control-label"
                >
                  {{ $t('components.notificationsSettings.modals.voltage') }}
                </label>
              </div>
              <input
                type="text"
                id="voltage-input"
                name="voltage"
                v-model="v$.notificationSetting.voltage.$model"
                :disabled="!isVoltageChecked"
                class="form-control"
                @blur="v$.notificationSetting.voltage.$touch()"
                :class="{
                  'text-muted': !isVoltageChecked,
                  'is-invalid':
                    v$.notificationSetting.voltage.$error &&
                    v$.notificationSetting.voltage.$dirty,
                }"
                :placeholder="!isVoltageChecked ? $t('components.notificationsSettings.modals.enterVoltage') : ''"
              >
              <!-- Validation message with space reserved below -->
              <div class="validation-space">
                <div
                  v-if="
                    v$.notificationSetting.voltage.$error &&
                      v$.notificationSetting.voltage.$dirty
                  "
                  class="invalid-feedback mt-2 d-block"
                >
                  <div
                    v-for="error in v$.notificationSetting.voltage.$errors"
                    :key="error.$uid"
                  >
                    {{ error.$message }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="pull-right">
        <button
          class="btn btn-default"
          @click="closeModal"
        >
          <i class="fa fa-remove" />
          {{ $t('components.vehicles.modals.exit') }}
        </button>
        <button
          class="btn btn-primary"
          @click="saveNotificationSetting"
        >
          <i class="fa fa-save" /> {{ $t('components.vehicles.modals.save') }}
        </button>
      </div>
    </template>
  </base-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import {requiredIf, numeric, helpers, maxValue} from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import BaseModal from './core/BaseModal.vue'
import { postNotificationSettings } from '@/crud/device/senders'

export default defineComponent({
  name: 'NotificationsSettingsModal',
  components: {
    BaseModal,
  },
  data() {
    return {
      notificationSetting: {
        send_by_email: false,
        send_by_sms: false,
        send_by_mercure: false,
        ignition: false,
        satellites: false,
        speed: null as number | null,
        voltage: null as number | null,
      },
      isSpeedChecked: false,
      isVoltageChecked: false,
      isEdit: false,
      imei: '',
    }
  },
  validations() {
    return {
      notificationSetting: {
        speed: {
          required: helpers.withMessage(
              this.$t('components.notificationsSettings.validations.required'),
              requiredIf(() => this.isSpeedChecked)
          ),
          numeric: helpers.withMessage(
              this.$t('components.notificationsSettings.validations.isNumber'),
              numeric
          ),
        },
        voltage: {
          required: helpers.withMessage(
              this.$t('components.notificationsSettings.validations.required'),
              requiredIf(() => this.isVoltageChecked)
          ),
          numeric: helpers.withMessage(
              this.$t('components.notificationsSettings.validations.isNumber'),
              numeric
          ),
          maxValue: helpers.withMessage(
              this.$t('components.notificationsSettings.validations.maxValue', { max: 90 }),
              maxValue(90)
          ),
        },
      },
    }
  },
  watch: {
    isSpeedChecked(val) {
      if (val) {
        // Checkbox checked
        this.notificationSetting.speed = 120
      } else {
        // Checkbox unchecked
        this.notificationSetting.speed = null
        this.v$.notificationSetting.speed.$reset()
      }
    },
    isVoltageChecked(val) {
      if (val) {
        // Checkbox checked
        this.notificationSetting.voltage = 8 // Default value in Volts
      } else {
        // Checkbox unchecked
        this.notificationSetting.voltage = null
        this.v$.notificationSetting.voltage.$reset()
      }
    },
  },
  methods: {
    openEditModal(data) {
      if (data && data.notification_settings) {
        this.imei = data.imei

        // Initialize notificationSetting with default values and overwrite with existing settings
        this.notificationSetting = {
          send_by_email: data.notification_settings.send_by_email ?? false,
          send_by_sms: data.notification_settings.send_by_sms ?? false,
          send_by_mercure: data.notification_settings.send_by_mercure ?? false,
          ignition: data.notification_settings.ignition ?? false,
          satellites: data.notification_settings.satellites ?? false,
          speed: data.notification_settings.speed ?? null,
          voltage: data.notification_settings.voltage ?? null,
        }

        // Convert voltage from mV to V for display
        if (this.notificationSetting.voltage !== null) {
          this.notificationSetting.voltage = this.notificationSetting.voltage / 1000
        }

        // Determine if checkboxes should be checked
        this.isSpeedChecked = this.notificationSetting.speed !== null
        this.isVoltageChecked = this.notificationSetting.voltage !== null

        // Set default values when checkbox is checked
        if (this.isSpeedChecked) {
          this.notificationSetting.speed = this.notificationSetting.speed || 120
        }

        if (this.isVoltageChecked) {
          this.notificationSetting.voltage = this.notificationSetting.voltage || 8
        }

        this.isEdit = true
      } else {
        this.resetSettings()
        this.isEdit = false
      }
      this.v$.$reset();
      (this.$refs.core as any).openModal()
    },
    resetSettings() {
      this.notificationSetting = {
        send_by_email: false,
        send_by_sms: false,
        send_by_mercure: false,
        ignition: false,
        satellites: false,
        speed: null,
        voltage: null,
      }
      this.isSpeedChecked = false
      this.isVoltageChecked = false
    },
    closeModal() {
      (this.$refs.core as any).closeModal()
    },
    saveNotificationSetting() {
      // Touch only the fields that are relevant
      if (this.isSpeedChecked) {
        this.v$.notificationSetting.speed.$touch()
      }
      if (this.isVoltageChecked) {
        this.v$.notificationSetting.voltage.$touch()
      }

      if (this.v$.$invalid) {
        return
      }

      // Ensure that when checkbox is unchecked, value is null
      if (!this.isSpeedChecked) {
        this.notificationSetting.speed = null
      } else if (!this.notificationSetting.speed) {
        // Set default if value is empty
        this.notificationSetting.speed = 120
      }

      if (!this.isVoltageChecked) {
        this.notificationSetting.voltage = null
      } else if (!this.notificationSetting.voltage) {
        // Set default if value is empty
        this.notificationSetting.voltage = 8
      }

      // Prepare payload
      const payload = { ...this.notificationSetting }

      // Convert voltage to mV before sending to server
      if (payload.voltage !== null) {
        payload.voltage = payload.voltage * 1000
      }

      const self = this

      postNotificationSettings(this.imei, payload)
          .then(() => {
            self.$emit('emittedRefreshGrid', true)
            self.$toast.success(
                self.$t('components.notificationsSettings.modals.successfullyEdited')
            )
            self.closeModal()
          })
          .catch(() => {
            self.$toast.error(self.$t('components.notificationsSettings.modals.error'))
          })
    },
  },
  setup() {
    const v$ = useVuelidate()
    return { v$ }
  },
})
</script>

<style lang="scss">
.notifications-settings-custom-modal {
  .custom_modal__dialog {
    width: 700px;

    .input-section {
      .form-check-input {
        width: 33px;
      }
    }
  }

  .form-check-input {
    margin-left: 0.5rem;
  }

  .control-label {
    margin-bottom: 0.5rem;
  }

  .text-muted {
    background-color: #e9ecef;
  }

  .validation-space {
    min-height: 24px;
  }

  .is-invalid + .invalid-feedback {
    display: block;
  }

  .invalid-feedback.d-block {
    display: block;
  }

  .mt-3 {
    margin-top: 1rem;
  }

  // Mobile responsive styles
  @media (max-width: 768px) {
    .custom_modal__dialog {
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      height: 100vh !important;
      max-height: 100vh !important;
      border-radius: 0 !important;
    }

    .custom_modal {
      .form-body {
        padding: 10px !important;
      }

      .row {
        margin-bottom: 8px !important;
      }

      .col-md-6 {
        margin-bottom: 8px !important;
        padding: 0 8px !important;
      }

      .control-label {
        font-size: 14px !important;
        margin-bottom: 4px !important;
        margin-right: 8px !important;
      }

      .form-check-input {
        width: 20px !important;
        height: 20px !important;
        margin-right: 8px !important;
      }

      .form-control {
        font-size: 14px !important;
        padding: 6px 8px !important;
        height: auto !important;
      }

      .input-section {
        .form-check {
          margin-bottom: 0 !important;
          padding-left: 0;
        }

        .form-check-input {
          width: 20px !important;
          height: 20px !important;
        }

        .form-check-label {
          margin-bottom: 0 !important;
        }

        .form-control {
          width: 100% !important;
          margin-top: 4px !important;
        }
      }

      .validation-space {
        min-height: 20px !important;
      }

      .invalid-feedback {
        font-size: 12px !important;
        margin-top: 4px !important;
      }

      .btn {
        font-size: 14px !important;
        padding: 8px 12px !important;
        margin: 0 4px !important;
      }

      .pull-right {
        text-align: center !important;
        padding: 10px !important;
      }
    }
  }
}
</style>
